import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateLocalized } from '../../utils/dateUtils';
import { getFreelancerSubtitle } from '../../utils/freelancerUtils';
import { getJobSelectionInfo } from '../../utils/jobUtils';
import { Job } from '../../types/job';
import { QuoteJob } from '../../types/quote';

interface JobInfoProps {
  job: Job | QuoteJob;
  className?: string;
}

const JobInfo: React.FC<JobInfoProps> = ({ job, className = '' }) => {
  const { t, i18n } = useTranslation(['jobs', 'common']);
  const selectionInfo = getJobSelectionInfo('selection_date' in job ? job : {}, t, i18n.language);

  return (
    <div className={`${className}`}>
      <h3 className="text-secondary text-xl font-bold md:my-4 mt-8">
        {t('project_info', { ns: 'jobs', defaultValue: 'Informations du projet' })}
      </h3>
      <div className="flex flex-col flex-wrap gap-x-8 gap-y-4 mb-8 text-sm text-secondary md:flex-row md:gap-y-1">
        <div className="flex flex-col">
          <span className="text-sm">{t('service_type', { ns: 'jobs' })} </span>
          <span className="font-bold text-[0.9375rem]">
            {getFreelancerSubtitle(
              { service_category_slugs: job.required_services.map(s => s.slug) },
              t
            )}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-sm">{t('form.location', { ns: 'jobs' })} </span>
          <span className="font-bold text-[0.9375rem]">{job.city?.name}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-sm">{t('form.onsite', { ns: 'jobs' })} </span>
          <span className="font-bold text-[0.9375rem]">
            {job.on_site
              ? t('form_fields.yes', { ns: 'common' })
              : t('form_fields.no', { ns: 'common' })}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-sm">{t('form.languages', { ns: 'jobs' })} </span>
          <span className="font-bold text-[0.9375rem] ">
            {job.languages?.map(l => t(`language.${l}`, { ns: 'common' })).join(', ')}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-sm ">{t('budget', { ns: 'jobs' })}</span>
          <span className="font-bold text-[0.9375rem]">
            {t(`budget_ranges.${job.budget_range.code}`, { ns: 'common' })}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-sm ">{t('form.start_date', { ns: 'jobs' })} </span>
          <span className="font-bold text-[0.9375rem]">
            {job.start_date &&
              formatDateLocalized(job.start_date, i18n.language, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
          </span>
        </div>
        {job.end_date && (
          <div className="flex flex-col">
            <span className="text-sm ">{t('form.end_date', { ns: 'jobs' })} </span>
            <span className="font-bold text-[0.9375rem]">
              {job.end_date &&
                formatDateLocalized(job.end_date, i18n.language, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
            </span>
          </div>
        )}
        {selectionInfo && (
          <div className="flex flex-col">
            <span className="text-sm ">{selectionInfo.label} </span>
            <span className="font-bold text-[0.9375rem]">{selectionInfo.value}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobInfo;
